import React from 'react';
import { styled } from '@compiled/react';
import {
	type Environment,
	fetchQuery,
	graphql,
	useRelayEnvironment,
	useFragment,
	useMutation,
	ConnectionHandler,
} from 'react-relay';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { PopupUserPicker, type Value, type OptionData, type User } from '@atlaskit/user-picker';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	approverSelector_addApproverMutation,
	approverSelector_addApproverMutation$data,
} from '@atlassian/jira-relay/src/__generated__/approverSelector_addApproverMutation.graphql';
import type { approverSelector_Query$data as Response } from '@atlassian/jira-relay/src/__generated__/approverSelector_Query.graphql';
import type { approverSelector_softwareReleasesApprovers$key } from '@atlassian/jira-relay/src/__generated__/approverSelector_softwareReleasesApprovers.graphql';
import {
	useAddVersionApproversExperienceStart,
	useAddVersionApproversExperienceFail,
	useAddVersionApproversExperienceSuccess,
} from '../../services/add-approver-experience-tracker/index.tsx';
import messages from './messages.tsx';

const isOptionData = (val: OptionData | null): val is OptionData =>
	val != null && typeof val.name === 'string' && typeof val.id === 'string';

export interface Props {
	jiraVersion: approverSelector_softwareReleasesApprovers$key;
	numberOfApprovers: number;
}

const createLoadQueryFunc =
	(environment: Environment, versionAri: string) => (searchText?: string) =>
		fetchQuery(
			environment,
			graphql`
				query approverSelector_Query(
					$versionAri: ID!
					$searchText: String
					$excludedAccountIds: [String!]
				) {
					jira @required(action: THROW) {
						version(id: $versionAri) @required(action: THROW) {
							... on JiraVersion {
								project @required(action: THROW) {
									suggestedApproversForJiraVersion(
										searchText: $searchText
										excludedAccountIds: $excludedAccountIds
									) {
										edges {
											node @required(action: THROW) {
												accountId @required(action: THROW)
												name @required(action: THROW)
												picture
												... on AtlassianAccountUser {
													extendedProfile {
														jobTitle
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				versionAri,
				searchText,
			},
			{
				fetchPolicy: 'store-or-network',
			},
		)
			.toPromise()
			.then(
				// @ts-expect-error - TS2345 - Argument of type '(response: Response) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
				(response: Response) => {
					const edges =
						response?.jira?.version?.project?.suggestedApproversForJiraVersion?.edges ?? [];
					return edges
						.filter(Boolean)
						.map((edge) => edge?.node)
						.map((user): OptionData | null =>
							user == null
								? null
								: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									({
										avatarUrl: user.picture,
										name: user.name,
										id: user.accountId,
										byline: user?.extendedProfile?.jobTitle ?? '',
										type: 'user',
									} as User),
						)
						.filter(isOptionData);
				},
			)
			.catch((error) => {
				fireErrorAnalytics({
					meta: {
						id: 'approverSelectorQuery',
						packageName: 'jiraSoftwareReleasesApprovers',
					},
					error,
				});
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				return [] as OptionData[];
			});

export const ApproverSelector = ({ numberOfApprovers, jiraVersion }: Props) => {
	const environment = useRelayEnvironment();
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const experienceStart = useAddVersionApproversExperienceStart();
	const experienceFail = useAddVersionApproversExperienceFail();
	const experienceSuccess = useAddVersionApproversExperienceSuccess();

	const version = useFragment<approverSelector_softwareReleasesApprovers$key>(
		graphql`
			fragment approverSelector_softwareReleasesApprovers on JiraVersion {
				id @required(action: THROW)
				__id
			}
		`,
		jiraVersion,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'selected',
		actionSubject: 'versionApprover',
	});
	const [addJiraVersionApprover] = useMutation<approverSelector_addApproverMutation>(graphql`
		mutation approverSelector_addApproverMutation(
			$connections: [ID!]!
			$input: JiraVersionAddApproverInput!
		) {
			jira {
				addJiraVersionApprover(input: $input) @optIn(to: ["JiraAddVersionApprover"]) {
					success
					approverEdge @prependEdge(connections: $connections) {
						node {
							id
							...taskApprover_softwareReleasesApprovers_jiraVersionApprover
						}
					}
					errors {
						message
					}
				}
			}
		}
	`);

	const connectionId = ConnectionHandler.getConnectionID(version.__id, 'version_approvers');
	const onMutationError = (error: Error) => {
		experienceFail('graphql error', error);
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.approvers.approver-selector',
				packageName: 'jiraSoftwareReleasesApprovers',
				teamName: 'fusion-solaris',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			title: formatMessage(messages.genericErrorFlagTitle),
			description: formatMessage(messages.genericErrorFlagDescription),
			type: 'error',
		});
	};
	const versionAri = version.id;
	const loadOptions = createLoadQueryFunc(environment, versionAri);

	const addApproverToVersion = (newApprover: OptionData) => {
		addJiraVersionApprover({
			variables: {
				input: {
					versionId: versionAri,
					approverAccountId: newApprover?.id,
				},
				connections: [connectionId],
			},

			onCompleted: (data: approverSelector_addApproverMutation$data) => {
				if (data.jira?.addJiraVersionApprover?.success !== true) {
					const responseErrors = data?.jira?.addJiraVersionApprover?.errors;
					const errorMessage =
						responseErrors?.[0]?.message ?? 'addVersionApprover graphql mutation failed';
					onMutationError(new Error(errorMessage));
				}
				fireUIAnalytics(analyticsEvent, 'versionApprover added', {
					isApproverSet: Boolean(newApprover?.id),
				});
				experienceSuccess();
			},
			onError: (error: Error) => {
				onMutationError(error);
			},
		});
		return newApprover;
	};

	return (
		<PlacholderStyleOverrider>
			<PopupUserPicker
				fieldId="jiraVersionApprovers"
				width={320}
				placeholder={formatMessage(messages.findUserLabel)}
				onChange={(value: Value) => {
					if (Array.isArray(value)) return;
					value == null ? null : addApproverToVersion(value);
				}}
				target={({ ...targetProps }) => (
					<Button
						{...targetProps}
						appearance="subtle"
						isDisabled={numberOfApprovers >= 100}
						iconBefore={<AddIcon size="small" label={formatMessage(messages.addApproverLabel)} />}
					/>
				)}
				loadOptions={loadOptions}
				placement="bottom-end"
				onFocus={() => experienceStart()}
			/>
		</PlacholderStyleOverrider>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlacholderStyleOverrider = styled.div(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__menu': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			zIndex: layers.modal,
		},
		/* FIXME: this style override is required for the placeholder styling to be compatible with react-select v4.
    Once JFE resolves to react-select v5, this style override can be removed (aka when the resolution for @atlaskit/select
    in the root package.json will be removed: https://ecosystem-platform.atlassian.net/browse/UNBOX-685). */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__value-container': {
			display: 'grid',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.fabric-user-picker__placeholder': {
				position: 'relative',
				top: 'initial',
				left: 'initial',
				transform: 'initial',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__single-value': {
			paddingLeft: token('space.100', '8px'),
			width: '88%',
		},
	},
	{
		marginLeft: token('space.negative.075', '-6px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__control:not(.fabric-user-picker__control--menu-is-open):not(.fabric-user-picker__control--is-focused)':
			{
				border: 'none',
				background: 'none',
				'&:hover': {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					backgroundColor: token('color.background.input.hovered', colors.N20A),
				},
			},
	},
);
